import { getAlgoliaResults } from "@algolia/autocomplete-js";
import { useI18n } from "vue-i18n";
import { useLanguages } from "@/core/composables";
import { prefixLocaleUrl } from "@/core/utilities";
import { addAlgoliaParametersToUrlFromSearchItem } from "@/shared/algolia-analytics/algolia-analytics";
import { getIndexName } from "@/shared/thorlabs/search";
import { getCdnUrl } from "@/shared/thorlabs/shared/image-resize";
import { searchClient } from "./searchClient";
import type { IAlgoliaFamilyPage } from "../searchTypes";

export function getFamilyPagesPlugin() {
  const { t } = useI18n();

  const { currentLocale, defaultLocale } = useLanguages();
  const familyPagesIndexName = getIndexName("familypages", currentLocale.value);

  return {
    getSources({ query }: { query: string }) {
      if (!query) {
        return [];
      }
      return [
        {
          sourceId: "familyPages",
          getItems() {
            return getAlgoliaResults({
              searchClient: searchClient.value,
              queries: [
                {
                  indexName: familyPagesIndexName,
                  params: {
                    query,
                    hitsPerPage: 3,
                    attributesToSnippet: ["name:10"],
                    snippetEllipsisText: "…",
                    filters: "isTechnicalResource:false",
                  },
                },
              ],
            });
          },
          templates: {
            header({
              html,
              state,
              items,
            }: {
              html: (literals: TemplateStringsArray, ...placeholders: unknown[]) => string;
              state: { query: string };
              items: IAlgoliaFamilyPage[];
            }) {
              return html`<div class="search-bar__header">
                ${items.length === 3
                  ? html`<h6>${t("autocomplete.search.top_3_family_matches")}</h6>`
                  : html`<h6>${t("autocomplete.search.top_family_matches")}</h6>`}
                <a
                  href="${prefixLocaleUrl(`/search?q=${state.query}`, currentLocale.value, defaultLocale.value)}"
                  class="hidden lg:block lg:absolute lg:top-0 lg:right-2"
                  onClick=${() => {
                    // Dispatch a custom DOM event to close the search dropdown
                    const event = new CustomEvent("closeSearchDropdown");
                    document.dispatchEvent(event);
                  }}
                  >${t("autocomplete.search.view_results")}</a
                >
              </div>`;
            },
            item({
              item,
              html,
            }: {
              html: (literals: TemplateStringsArray, ...placeholders: unknown[]) => string;
              item: IAlgoliaFamilyPage;
            }) {
              return html`<a
                href="${addAlgoliaParametersToUrlFromSearchItem(
                  prefixLocaleUrl(item.url, currentLocale.value, defaultLocale.value),
                  item,
                )}"
                class="family-match--item"
                onClick=${() => {
                  // Dispatch a custom DOM event to close the search dropdown
                  const event = new CustomEvent("closeSearchDropdown");
                  document.dispatchEvent(event);
                }}
              >
                <div class="family-match--image">
                  <img src="${getCdnUrl(item.imageUrl)}" width="184" height="112" alt="" role="presentation" />
                </div>
                <div class="family-match--content-wrapper">
                  <div class="family-match--content">
                    <div class="family-match--name">
                      <span dangerouslySetInnerHTML=${{ __html: item.name }}></span>
                    </div>
                    ${item.visNavName
                      ? html`<div class="family-match--info">
                          <span class="inline-flex size-3 lg:hidden whitespace-nowrap mr-1 top-[1px] relative">
                            <svg>
                              <use href="/static/thorlabs/icons/basic/arrow-menu.svg?#icon"></use>
                            </svg>
                          </span>
                          <span
                            dangerouslySetInnerHTML=${{
                              __html: t("autocomplete.search.family_in_category", { category: item.visNavName }),
                            }}
                          ></span>
                        </div>`
                      : ""}
                  </div>
                  <div class="family-match--link">${t("autocomplete.search.view_product_family")}</div>
                </div>
              </a>`;
            },
            noResults() {
              return t("autocomplete.search.empty_family_pages");
            },
          },
        },
      ];
    },
  };
}
